import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CountrySelect } from "@/components/ui/country-select";
import { useUpdateCompany, useDeleteCompany, CreateCompanyData } from "@/hooks/use-companies";
import { useContacts } from "@/hooks/use-contacts";
import { useStaff } from "@/hooks/use-staff";
import { usePartners } from "@/hooks/use-partners";
import { useActivitiesByOrganization } from "@/hooks/use-activities";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { useSyncOrganizationProfile } from "@/hooks/use-sync-organization-profile";
import { useOrganizationSync } from "@/hooks/use-organization-sync";
import { ActivitiesTab } from "./ActivitiesTab";
import { AssignAdminModal } from "./AssignAdminModal";
import { AvatarUpload } from "./AvatarUpload";
import { Building2, Trash2, Users, UserCircle, Calendar, Settings, ExternalLink, Copy, Eye, Globe, Truck, FileCheck } from "lucide-react";
import { KeyRound } from "lucide-react";
import { EnvironmentCredentialsCard } from "@/components/organization/EnvironmentCredentialsCard";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ShowcaseQRCode } from "@/components/organization/ShowcaseQRCode";
import { ShowcaseAccessCodeSettings } from "@/components/organization/ShowcaseAccessCodeSettings";
import { normalizeShowcaseAccessCode } from "@/utils/showcaseAccess";
import { INDUSTRY_OPTIONS } from "@/constants/organization";
const companySchema = z.object({
  name: z.string().min(1, "Company name is required"),
  website: z.string().optional(),
  industry: z.string().optional(),
  address: z.string().optional(),
  country: z.string().optional(),
  division: z.string().optional(),
  notes: z.string().optional(),
  logo_url: z.string().optional(),
  description: z.string().optional(),
  opening_times: z.string().optional(),
  marketing_banner_url: z.string().optional(),
  image_1_url: z.string().optional(),
  image_2_url: z.string().optional(),
  image_3_url: z.string().optional(),
  google_places_id: z.string().optional(),
  slug: z.string().optional(),
  // Add missing database fields to prevent sync conflicts
  size: z.string().optional(),
  company_id: z.string().optional(),
  visibility: z.string().optional(),
  relationship_status: z.string().optional(),
  color_palette: z.any().optional(),
  internal_owner_id: z.string().optional(),
  external_owner_id: z.string().optional(),
  enable_fleet_mode: z.boolean().optional(),
  // MSA fields
  has_msa: z.boolean().optional(),
  msa_reference: z.string().optional(),
  enable_customer_admin: z.boolean().optional(),
  enable_device_diagnosis: z.boolean().optional(),
  enable_api_configuration: z.boolean().optional(),
  // Custom AI Workflow fields
  custom_workflow_id: z.string().optional(),
  custom_workflow_description: z.string().optional(),
  // Optional showcase access code
  showcase_access_code: z.string().optional(),
  showcase_access_code_enabled: z.boolean().optional()
});
interface OrganizationEditModalProps {
  organization: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export const OrganizationEditModal = ({
  organization,
  open,
  onOpenChange
}: OrganizationEditModalProps) => {
  const [assignAdminModalOpen, setAssignAdminModalOpen] = useState(false);
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
  const [pendingVisibility, setPendingVisibility] = useState<'public' | 'private'>('private');
  const queryClient = useQueryClient();
  const deleteCompany = useDeleteCompany();
  const syncOrganizationProfile = useSyncOrganizationProfile();
  const organizationSync = useOrganizationSync();
  const {
    data: contacts = [],
    refetch: refetchContacts
  } = useContacts();
  const {
    data: staff = []
  } = useStaff();
  const {
    data: partners = []
  } = usePartners();
  const {
    data: activities = [],
    isLoading: activitiesLoading
  } = useActivitiesByOrganization(organization.id);
  const {
    data: hasCrmAccess
  } = useCrmAccess();
  const {
    user
  } = useAuth();

  // Get user's organization to check if this is their own org
  const {
    data: userOrganization
  } = useQuery({
    queryKey: ['user-organization', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const {
        data
      } = await supabase.from('user_roles').select('organization_id').eq('user_id', user.id).maybeSingle();
      return data?.organization_id || null;
    },
    enabled: !!user?.id
  });
  const isOwnOrganization = userOrganization === organization.id;

  // Superuser role check — controls visibility of the API Environments tab.
  const { data: userRole } = useQuery({
    queryKey: ["user-role", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase.rpc("get_user_role", { user_id: user.id });
      return data as string | null;
    },
    enabled: !!user?.id,
  });
  const isSuperuser = userRole === "superuser";

  // Initialize updateCompany hook after isOwnOrganization is defined
  const updateCompany = useUpdateCompany({
    syncToBuildhip: !isOwnOrganization
  });

  // Filter contacts that belong to this organization
  const organizationContacts = contacts.filter(contact => contact.organization_id === organization.id);

  // Filter admin contacts - check for internal_owner_id or admin-related job titles
  const adminContacts = organizationContacts.filter(contact => {
    const hasInternalOwner = !!contact.internal_owner_id;
    const jobTitle = contact.job_title?.toLowerCase() || '';
    const hasAdminTitle = jobTitle.includes('admin') || jobTitle.includes('administrator') || jobTitle.includes('director') || jobTitle.includes('ceo') || jobTitle.includes('managing director') || jobTitle.includes('manager');
    const isAdmin = hasInternalOwner || hasAdminTitle;
    if (isAdmin) {
      console.log("Admin contact found:", {
        name: `${contact.first_name} ${contact.last_name}`,
        job_title: contact.job_title,
        internal_owner_id: contact.internal_owner_id,
        hasInternalOwner,
        hasAdminTitle
      });
    }
    return isAdmin;
  });
  const form = useForm<CreateCompanyData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: organization.name || "",
      website: organization.website || "",
      industry: organization.industry || "",
      address: organization.address || "",
      country: organization.country || "",
      division: organization.division || "",
      notes: organization.notes || "",
      logo_url: organization.logo_url || "",
      description: organization.description || "",
      opening_times: organization.opening_times || "",
      marketing_banner_url: organization.marketing_banner_url || "",
      image_1_url: organization.image_1_url || "",
      image_2_url: organization.image_2_url || "",
      image_3_url: organization.image_3_url || "",
      google_places_id: organization.google_places_id || "",
      slug: organization.slug || "",
      internal_owner_id: organization.internal_owner_id || undefined,
      external_owner_id: organization.external_owner_id || undefined,
      visibility: organization.visibility || "private",
      enable_fleet_mode: organization.enable_fleet_mode || false,
      has_msa: organization.has_msa || false,
      msa_reference: organization.msa_reference || "",
      enable_customer_admin: !!(organization as any).enable_customer_admin,
      enable_device_diagnosis: !!(organization as any).enable_device_diagnosis,
      enable_api_configuration: !!(organization as any).enable_api_configuration,
      custom_workflow_id: organization.custom_workflow_id || "",
      custom_workflow_description: organization.custom_workflow_description || "",
      showcase_access_code: (organization as any).showcase_access_code || "",
      showcase_access_code_enabled: !!(organization as any).showcase_access_code_enabled
    }
  });
  const onSubmit = async (data: CreateCompanyData) => {
    console.log('🔄 CRM: Starting organization update...', {
      organizationId: organization.id,
      data
    });
    const normalizedCode = normalizeShowcaseAccessCode(data.showcase_access_code);
    const payload: CreateCompanyData = {
      ...data,
      showcase_access_code: normalizedCode || null as any,
      showcase_access_code_enabled: !!data.showcase_access_code_enabled && !!normalizedCode
    };
    updateCompany.mutate({
      id: organization.id,
      data: payload
    }, {
      onSuccess: async () => {
        console.log('✅ CRM: Organization updated, triggering sync...');
        toast.success('Organization updated successfully');

        // Invalidate profile completeness queries to update percentage
        queryClient.invalidateQueries({
          queryKey: ['organization-profile-completeness', organization.id]
        });
        queryClient.invalidateQueries({
          queryKey: ['organization-profile-notifications']
        });

        // If this is the user's own organization, use the specialized sync
        // For other organizations, the general sync is handled by updateCompany hook
        if (isOwnOrganization) {
          try {
            await syncOrganizationProfile.mutateAsync({
              organizationId: organization.id,
              profileData: data
            });
            console.log('✅ CRM: Organization profile sync completed successfully');
          } catch (syncError) {
            console.error('❌ CRM: Organization profile sync failed:', syncError);
            toast.error('Organization updated but profile sync failed');
          }
        }
        onOpenChange(false);
      },
      onError: error => {
        console.error('💥 CRM: Organization update failed:', error);
        toast.error(`Failed to update organization: ${error.message}`);
      }
    });
  };
  const handleDelete = () => {
    deleteCompany.mutate(organization.id, {
      onSuccess: () => {
        onOpenChange(false);
      }
    });
  };
  const getInitials = (name: string) => {
    if (!name) return "ORG";
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader className="bg-white -mx-6 -mt-6 px-6 py-4 rounded-t-xl">
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Edit Organization
            {isOwnOrganization && <Badge variant="secondary" className="ml-2">Your Organization</Badge>}
          </DialogTitle>
          {isOwnOrganization && <div className="mt-2">
              <Link to="/dashboard/organisation-profile" className="text-muted-foreground hover:text-foreground text-sm flex items-center gap-1">
                <Settings className="h-4 w-4" />
                Manage Full Profile & API Settings
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>}
        </DialogHeader>
        
        <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className={`grid w-full ${isSuperuser ? "grid-cols-6" : "grid-cols-5"}`}>
            <TabsTrigger value="details" className="data-[state=active]:bg-lime-button data-[state=active]:text-black">Basic Info</TabsTrigger>
            <TabsTrigger value="branding" className="data-[state=active]:bg-lime-button data-[state=active]:text-black">Branding</TabsTrigger>
            <TabsTrigger value="admins">
              <UserCircle className="h-4 w-4 mr-2" />
              Admins ({adminContacts.length})
            </TabsTrigger>
            <TabsTrigger value="contacts">
              <Users className="h-4 w-4 mr-2" />
              Contacts ({organizationContacts.length})
            </TabsTrigger>
            <TabsTrigger value="activities">
              <Calendar className="h-4 w-4 mr-2" />
              Activities ({activities.length})
            </TabsTrigger>
            {isSuperuser && (
              <TabsTrigger value="api-environments">
                <KeyRound className="h-4 w-4 mr-2" />
                API Environments
              </TabsTrigger>
            )}
          </TabsList>
          
          <TabsContent value="details" className="flex-1 overflow-auto">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-6">
                <div className="space-y-2">
                  <FormLabel className="text-foreground font-medium">Organization Logo</FormLabel>
                  <AvatarUpload currentImageUrl={form.watch("logo_url")} onImageChange={imageUrl => form.setValue("logo_url", imageUrl)} fallbackText={getInitials(form.watch("name"))} size="lg" />
                </div>

                <FormField control={form.control} name="name" render={({
                field
              }) => <FormItem>
                      <FormLabel>Organization Name *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter organization name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                
                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="website" render={({
                  field
                }) => <FormItem>
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  
                   <FormField control={form.control} name="industry" render={({
                   field
                 }) => <FormItem>
                         <FormLabel>Industry</FormLabel>
                         <Select onValueChange={field.onChange} value={field.value || undefined}>
                           <FormControl>
                             <SelectTrigger>
                               <SelectValue placeholder="Select industry" />
                             </SelectTrigger>
                           </FormControl>
                           <SelectContent>
                             {INDUSTRY_OPTIONS.map((option) => (
                               <SelectItem key={option} value={option}>
                                 {option}
                               </SelectItem>
                             ))}
                           </SelectContent>
                         </Select>
                         <FormMessage />
                       </FormItem>} />
                </div>
                
                <FormField control={form.control} name="country" render={({
                field
              }) => <FormItem>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <CountrySelect {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                
                <FormField control={form.control} name="address" render={({
                field
              }) => <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Enter full address" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                
                <FormField control={form.control} name="division" render={({
                field
              }) => <FormItem>
                      <FormLabel>Division</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Business division or department" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="description" render={({
                field
              }) => <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Organization description" className="min-h-[80px]" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={form.control} name="opening_times" render={({
                field
              }) => <FormItem>
                      <FormLabel>Opening Times</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="e.g., Mon-Fri: 9:00-17:00" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                {/* Organization Visibility Toggle */}
                <div className="space-y-4 p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        <FormLabel className="text-base font-semibold">Organization Visibility</FormLabel>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {form.watch('visibility') === 'public' ? 'This organization is visible to the public' : 'This organization is only visible to authorized users'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">Private</span>
                      <FormField control={form.control} name="visibility" render={({
                      field
                    }) => <FormItem>
                            <FormControl>
                              <Switch checked={field.value === 'public'} onCheckedChange={checked => {
                          const newValue = checked ? 'public' : 'private';
                          setPendingVisibility(newValue);
                          setVisibilityDialogOpen(true);
                        }} />
                            </FormControl>
                          </FormItem>} />
                      <span className="text-sm font-medium">Public</span>
                    </div>
                  </div>
                </div>

                {/* Fleet Mode Toggle - Superuser Only */}
                {hasCrmAccess && <div className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Truck className="h-4 w-4" />
                          <FormLabel className="text-base font-semibold">Fleet Mode</FormLabel>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Enable image credit pooling for this organisation
                        </p>
                      </div>
                      <FormField control={form.control} name="enable_fleet_mode" render={({
                    field
                  }) => <FormItem>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </FormItem>} />
                    </div>
                  </div>}

                {/* MSA Settings - Superuser Only */}
                {hasCrmAccess && <div className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FileCheck className="h-4 w-4" />
                          <FormLabel className="text-base font-semibold">Master Service Agreement (MSA)</FormLabel>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Enable direct deal-to-order conversion without proposal acceptance
                        </p>
                      </div>
                      <FormField control={form.control} name="has_msa" render={({
                    field
                  }) => <FormItem>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </FormItem>} />
                    </div>
                    
                    {form.watch('has_msa') && (
                      <FormField control={form.control} name="msa_reference" render={({
                        field
                      }) => <FormItem>
                              <FormLabel>MSA Reference (optional)</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="e.g. MSA-2025-001" />
                              </FormControl>
                              <p className="text-xs text-muted-foreground">
                                Reference number or identifier for the Master Service Agreement
                              </p>
                              <FormMessage />
                            </FormItem>} />
                    )}
                  </div>}

                {/* Admin menu pages - Superuser Only */}
                {hasCrmAccess && <div className="space-y-3 p-4 border rounded-lg">
                    <div className="space-y-1">
                      <FormLabel className="text-base font-semibold">Admin menu pages</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Choose which extra pages this organisation's admins can see. Off by default.
                      </p>
                    </div>
                    {([
                      ["enable_customer_admin", "Customer Management"],
                      ["enable_device_diagnosis", "Device Diagnosis"],
                      ["enable_api_configuration", "API Configuration"],
                    ] as const).map(([name, label]) => (
                      <FormField key={name} control={form.control} name={name as any} render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0">
                          <FormControl>
                            <Checkbox checked={!!field.value} onCheckedChange={(v) => field.onChange(v === true)} />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">{label}</FormLabel>
                        </FormItem>
                      )} />
                    ))}
                  </div>}

                {/* Custom AI Workflow - Superuser Only */}
                {hasCrmAccess && <div className="space-y-4 p-4 border rounded-lg">
                    <div className="space-y-1">
                      <FormLabel className="text-base font-semibold">Custom AI Workflow</FormLabel>
                      <p className="text-sm text-muted-foreground">
                        Configure a bespoke AI workflow for this organisation (synced to BuildShip).
                      </p>
                    </div>
                    <FormField control={form.control} name="custom_workflow_id" render={({
                      field
                    }) => <FormItem>
                            <FormLabel>Custom Workflow ID</FormLabel>
                            <FormControl>
                              <Input {...field} placeholder="e.g. wf_abc123" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                    <FormField control={form.control} name="custom_workflow_description" render={({
                      field
                    }) => <FormItem>
                            <FormLabel>Custom Workflow Description</FormLabel>
                            <FormControl>
                              <Textarea {...field} placeholder="Describe what this custom workflow does" className="min-h-[80px]" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                  </div>}

                <FormField control={form.control} name="slug" render={({
                field
              }) => <FormItem>
                      <FormLabel>Showcase URL Slug</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., leicestershire-golf-club" className="font-mono text-sm" />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        URL-friendly identifier for the showcase page. Leave empty to auto-generate from organization name.
                      </p>
                      <FormMessage />
                    </FormItem>} />

                {/* Public Showcase Section */}
                {form.watch('visibility') === 'public' && form.watch('slug') && <div className="space-y-4 p-4 bg-muted rounded-lg border">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold">Public Showcase URL</h4>
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        <Eye className="h-3 w-3 mr-1" />
                        Public
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      This organization's cameras are publicly visible. Share this link to allow public access.
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-4 items-start">
                      <div className="flex gap-2">
                        <Input readOnly value={`${window.location.origin}/showcase/${form.watch('slug')}`} className="font-mono text-sm" />
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/showcase/${form.watch('slug')}`);
                      toast.success("Showcase URL copied to clipboard");
                    }}>
                          <Copy className="h-4 w-4 mr-2" />
                          Copy
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => window.open(`/showcase/${form.watch('slug')}`, '_blank')}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </div>
                      <ShowcaseQRCode organizationId={form.watch('slug') || organization.id} organizationName={organization.name} size={200} />
                    </div>

                    <ShowcaseAccessCodeSettings
                      enabled={!!form.watch('showcase_access_code_enabled')}
                      code={(form.watch('showcase_access_code') as string) || ''}
                      showcaseUrl={`${window.location.origin}/showcase/${form.watch('slug')}`}
                      organizationName={organization.name}
                      onEnabledChange={value => form.setValue('showcase_access_code_enabled', value, { shouldDirty: true })}
                      onCodeChange={value => form.setValue('showcase_access_code', value, { shouldDirty: true })}
                    />
                  </div>}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField control={form.control} name="internal_owner_id" render={({
                  field
                }) => <FormItem>
                        <FormLabel>Internal Owner (Staff)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select staff member" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">No staff owner</SelectItem>
                            {staff.map(member => <SelectItem key={member.id} value={member.id}>
                                {member.first_name} {member.last_name}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={form.control} name="external_owner_id" render={({
                  field
                }) => <FormItem>
                        <FormLabel>External Owner (Partner)</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || undefined}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select partner" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="none">No partner owner</SelectItem>
                            {partners.map(partner => <SelectItem key={partner.id} value={partner.id}>
                                {partner.first_name} {partner.last_name}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <FormField control={form.control} name="notes" render={({
                field
              }) => <FormItem>
                      <FormLabel>Internal Notes</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Internal notes about the organization (not visible to customers)" className="min-h-[100px]" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
                
                <div className="flex justify-between">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Organization
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Organization</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this organization? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  
                  <div className="space-x-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                      Cancel
                    </Button>
                     <Button type="submit" disabled={updateCompany.isPending} className="bg-lime-button text-black hover:bg-teal-light">
                       {updateCompany.isPending ? "Updating..." : "Update Organization"}
                     </Button>
                  </div>
                </div>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="branding" className="flex-1 overflow-auto">
            <Form {...form}>
              <div className="space-y-4 p-6">
                <h3 className="text-lg font-semibold">Branding & Marketing</h3>
                <p className="text-sm text-muted-foreground">
                  Manage visual branding elements. For advanced branding settings and API configuration, 
                  {isOwnOrganization ? <Link to="/dashboard/organisation-profile" className="text-blue-600 hover:underline ml-1">
                      visit the Organization Profile page
                    </Link> : " contact the organization admin"}.
                </p>

                <FormField control={form.control} name="marketing_banner_url" render={({
                field
              }) => <FormItem>
                      <FormLabel>Marketing Banner URL</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="https://example.com/banner.jpg" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <div className="grid grid-cols-3 gap-4">
                  <FormField control={form.control} name="image_1_url" render={({
                  field
                }) => <FormItem>
                        <FormLabel>Image 1 URL</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com/image1.jpg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={form.control} name="image_2_url" render={({
                  field
                }) => <FormItem>
                        <FormLabel>Image 2 URL</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com/image2.jpg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={form.control} name="image_3_url" render={({
                  field
                }) => <FormItem>
                        <FormLabel>Image 3 URL</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="https://example.com/image3.jpg" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <FormField control={form.control} name="google_places_id" render={({
                field
              }) => <FormItem>
                      <FormLabel>Google Places ID</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="ChIJN1t_tDeuEmsRUsoyG83frY4" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                {organization.api_token_status && <div className="border rounded-lg p-4 bg-accent">
                    <h4 className="font-medium mb-2">API Configuration</h4>
                    <div className="text-sm text-muted-foreground">
                      <p>API Token Status: <Badge variant={organization.api_token_status === 'active' ? 'default' : 'secondary'}>{organization.api_token_status}</Badge></p>
                      {organization.api_token_created_at && <p>Created: {new Date(organization.api_token_created_at).toLocaleDateString()}</p>}
                      {organization.api_token_last_used_at && <p>Last Used: {new Date(organization.api_token_last_used_at).toLocaleDateString()}</p>}
                      {isOwnOrganization && <Link to="/dashboard/organisation-profile" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
                          Manage API settings →
                        </Link>}
                    </div>
                  </div>}

                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button onClick={form.handleSubmit(onSubmit)} disabled={updateCompany.isPending} className="bg-lime-button text-foreground hover:bg-teal-primary">
                    {updateCompany.isPending ? "Updating..." : "Update Branding"}
                  </Button>
                </div>
              </div>
            </Form>
          </TabsContent>

          <TabsContent value="admins" className="flex-1 overflow-auto">
            <div className="space-y-4 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Administrators at {organization.name}</h3>
                <Button variant="outline" size="sm" onClick={() => setAssignAdminModalOpen(true)} className="bg-lime-button text-black hover:bg-lime-button/90">
                  <Users className="h-4 w-4 mr-2" />
                  Add Admin
                </Button>
              </div>
              
              {adminContacts.length === 0 ? <div className="text-center py-8">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No administrators assigned to this organization.</p>
                  <p className="text-sm text-muted-foreground">Click "Add Admin" to assign an existing contact as an administrator.</p>
                </div> : <div className="space-y-2">
                  {adminContacts.map(contact => <div key={contact.id} className="p-3 border rounded-lg">
                      <div className="font-medium">{contact.first_name} {contact.last_name}</div>
                      <div className="text-sm text-muted-foreground">{contact.email}</div>
                      {contact.job_title && <div className="text-sm text-muted-foreground">{contact.job_title}</div>}
                      {contact.phone && <div className="text-sm text-muted-foreground">{contact.phone}</div>}
                    </div>)}
                </div>}
            </div>
          </TabsContent>
          
          <TabsContent value="contacts" className="flex-1 overflow-auto">
            <div className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">Contacts at {organization.name}</h3>
              {organizationContacts.length === 0 ? <p className="text-muted-foreground">No contacts assigned to this organization.</p> : <div className="space-y-2">
                  {organizationContacts.map(contact => <div key={contact.id} className="p-3 border rounded-lg">
                      <div className="font-medium">{contact.first_name} {contact.last_name}</div>
                      <div className="text-sm text-muted-foreground">{contact.email}</div>
                      {contact.job_title && <div className="text-sm text-muted-foreground">{contact.job_title}</div>}
                    </div>)}
                </div>}
            </div>
          </TabsContent>

          <TabsContent value="activities" className="flex-1 overflow-auto">
            <ActivitiesTab activities={activities} isLoading={activitiesLoading} title="Organization Activities" />
          </TabsContent>

          {isSuperuser && (
            <TabsContent value="api-environments" className="flex-1 overflow-auto">
              <div className="space-y-8 p-6">
                <EnvironmentCredentialsCard
                  organizationId={organization.id}
                  environment="development"
                  title="Development Environment"
                  description="Sandbox credentials for building and iterating against Snapshot APIs without touching production devices."
                  canEdit={isSuperuser}
                />
                <EnvironmentCredentialsCard
                  organizationId={organization.id}
                  environment="test"
                  title="Test Environment"
                  description="Pre-production credentials for end-to-end integration testing before promoting changes live."
                  canEdit={isSuperuser}
                />
              </div>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>

      <AssignAdminModal open={assignAdminModalOpen} onOpenChange={setAssignAdminModalOpen} organizationId={organization.id} organizationName={organization.name} onSuccess={() => {
      refetchContacts();
      // Force a small delay to ensure the refetch completes before closing
      setTimeout(() => {
        console.log("Contacts refetched after admin assignment");
      }, 100);
    }} />

      {/* Visibility Change Confirmation Dialog */}
      <AlertDialog open={visibilityDialogOpen} onOpenChange={setVisibilityDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change Organization Visibility to {pendingVisibility === 'public' ? 'Public' : 'Private'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingVisibility === 'public' ? <>
                  <p className="mb-2">Making this organization public will:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Make all camera feeds publicly visible</li>
                    <li>Expose device names, locations, and past images</li>
                    <li>Allow anyone to access the showcase page</li>
                    <li>Display organization details to the public</li>
                  </ul>
                  <p className="mt-2 font-semibold">Are you sure you want to proceed?</p>
                </> : <>
                  <p className="mb-2">Making this organization private will:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Hide all camera feeds from public view</li>
                    <li>Make the showcase page inaccessible</li>
                    <li>Require authentication to view organization data</li>
                    <li>Break any existing public links</li>
                  </ul>
                  <p className="mt-2 font-semibold">Confirm this change?</p>
                </>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
            form.setValue('visibility', pendingVisibility);
            setVisibilityDialogOpen(false);
            toast.info(`Organization visibility will be changed to ${pendingVisibility} when you save`);
          }}>
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>;
};